import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { NavigationGuardProvider } from "./contexts/NavigationGuardContext";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import { initApi } from "./lib/api";

// Layouts
import MainLayout from "./layouts/MainLayout";

// Pages
import LoginPage from "./pages/LoginPage";
import SetPasswordPage from "./pages/SetPasswordPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import PublicSalApprovalPage from "./pages/PublicSalApprovalPage";
import DashboardPage from "./pages/DashboardPage";
import LeadsPage from "./pages/LeadsPage";
import LeadDetailPage from "./pages/LeadDetailPage";
import CalendarPage from "./pages/CalendarPage";
import EstimateDetailPage from "./pages/EstimateDetailPage";
import EstimatesPage from "./pages/EstimatesPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import SubcontractDetailPage from "./pages/SubcontractDetailPage";
import UsersPage from "./pages/UsersPage";
import PriceListPage from "./pages/PriceListPage";
import ContractsPage from "./pages/ContractsPage";
import ContractDetailPage from "./pages/ContractDetailPage";
import SettingsPage from "./pages/SettingsPage";
import StatsPage from "./pages/StatsPage";
import CommissionsPage from "./pages/CommissionsPage";
import ProfilePage from "./pages/ProfilePage";
import ExtraDetailPage from "./pages/ExtraDetailPage";
import PaymentSettingsPage from "./pages/PaymentSettingsPage";
import PaymentsDashboardPage from "./pages/PaymentsDashboardPage";
import SuppliersPage from "./pages/SuppliersPage";
import FurnitureDashboardPage from "./pages/FurnitureDashboardPage";
import SystemHealthPage from "./pages/SystemHealthPage";
import AuditLogPage from "./pages/AuditLogPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import WhatsAppPage from "./pages/WhatsAppPage";
import DataIntegrityPage from "./pages/DataIntegrityPage";

// Protected Route Component (base authentication)
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user, hasRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <div className="relative mx-auto mb-4">
            <img 
              src="/images/abitalo-icon.png" 
              alt="ABITALO®" 
              className="w-16 h-16 object-contain"
            />
          </div>
          <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full mx-auto" />
          <p className="mt-4 text-[#737373]">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !hasRole(allowedRoles)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Permission Protected Route - usa permessi dinamici da db.permissions
const PermissionProtectedRoute = ({ children, permissionKey, allowedRoles, fallbackPath = "/" }) => {
  const { isAuthenticated, loading, hasPermission, permissionsLoading, permissionsLoaded, user } = useAuth();

  // Attende sia la sessione (loading) sia il PRIMO caricamento permessi.
  // `permissionsLoaded` chiude la race al full page load / refresh (deep-link):
  // senza di esso, un utente autenticato con permessi non ancora caricati veniva
  // valutato con matrice vuota e rediretto erroneamente al fallback.
  if (loading || permissionsLoading || (isAuthenticated && !permissionsLoaded)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <div className="relative mx-auto mb-4">
            <img 
              src="/images/abitalo-icon.png" 
              alt="ABITALO®" 
              className="w-16 h-16 object-contain"
            />
          </div>
          <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full mx-auto" />
          <p className="mt-4 text-[#737373]">Verifica permessi...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Verifica permesso dinamico
  if (permissionKey && !hasPermission(permissionKey)) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Verifica ruoli espliciti (alternativa a permissionKey — usata quando la route
  // deve essere accessibile a specifici ruoli anche senza matrice permessi coincidente).
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Initializer Component
const AppInitializer = ({ children }) => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        await initApi.initData();
      } catch (error) {
        // Already initialized or error - that's ok
      }
      setInitialized(true);
    };
    init();
  }, []);

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <div className="w-12 h-12 bg-[#FF7819] rounded-xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>a</span>
          </div>
          <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full mx-auto" />
          <p className="mt-4 text-[#737373]">Inizializzazione...</p>
        </div>
      </div>
    );
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NavigationGuardProvider>
          <AppInitializer>
            <div className="App">
              <ErrorBoundary>
              <Routes>
                {/* Public Routes */}
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <LoginPage />
                    </PublicRoute>
                  }
                />
                
                {/* Set Password (Public - no auth required) */}
                <Route
                  path="/set-password"
                  element={<SetPasswordPage />}
                />
                
                {/* Forgot Password (Public) */}
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPage />}
                />

                {/* R.6.1 · Public SAL Approval (no auth) */}
                <Route
                  path="/public/sal/:token"
                  element={<PublicSalApprovalPage />}
                />

                {/* Protected Routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  {/* Dashboard - view_dashboard */}
                  <Route index element={
                    <PermissionProtectedRoute permissionKey="view_dashboard">
                      <DashboardPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Lead - view_leads */}
                  <Route path="leads" element={
                    <PermissionProtectedRoute permissionKey="view_leads">
                      <LeadsPage />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="leads/:id" element={
                    <PermissionProtectedRoute permissionKey="view_leads">
                      <LeadDetailPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Calendario - view_calendar */}
                  <Route path="calendar" element={
                    <PermissionProtectedRoute permissionKey="view_calendar">
                      <CalendarPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Preventivi - view_estimates */}
                  <Route path="estimates" element={
                    <PermissionProtectedRoute permissionKey="view_estimates">
                      <EstimatesPage />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="estimates/:id" element={
                    <PermissionProtectedRoute permissionKey="view_estimates">
                      <EstimateDetailPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Progetti - manage_projects */}
                  <Route path="projects" element={
                    <PermissionProtectedRoute permissionKey="manage_projects">
                      <ProjectsPage />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="projects/:id" element={
                    <PermissionProtectedRoute permissionKey="manage_projects">
                      <ProjectDetailPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Utenti - manage_users */}
                  <Route path="users" element={
                    <PermissionProtectedRoute permissionKey="manage_users">
                      <UsersPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Listino Prezzi - view_price_list */}
                  <Route path="price-list" element={
                    <PermissionProtectedRoute permissionKey="view_price_list">
                      <PriceListPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Contratti - view_contracts */}
                  <Route path="contracts" element={
                    <PermissionProtectedRoute permissionKey="view_contracts">
                      <ContractsPage />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="contracts/:id" element={
                    <PermissionProtectedRoute permissionKey="view_contracts">
                      <ContractDetailPage />
                    </PermissionProtectedRoute>
                  } />
                  {/* F.6.2 · Dettaglio Contratto di Subappalto (tab Modifiche Contrattuali) */}
                  <Route path="subcontracts/:id" element={
                    <PermissionProtectedRoute permissionKey="view_projects">
                      <SubcontractDetailPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Impostazioni - view_settings */}
                  <Route
                    path="settings/*"
                    element={
                      <PermissionProtectedRoute permissionKey="view_settings">
                        <Routes>
                          <Route index element={<SettingsPage />} />
                          <Route path="admin/payment-settings" element={<PaymentSettingsPage />} />
                        </Routes>
                      </PermissionProtectedRoute>
                    }
                  />

                  {/* Anagrafica Fornitori - Admin/PM/Administration
                      (Sidebar la mostra solo ad Admin/Administration; PM accede via URL/wizard) */}
                  <Route path="admin/suppliers" element={
                    <PermissionProtectedRoute allowedRoles={['admin', 'administration', 'project_manager']}>
                      <SuppliersPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Statistiche - view_stats */}
                  <Route path="stats" element={
                    <PermissionProtectedRoute permissionKey="view_stats">
                      <StatsPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Analytics - view_stats (stesso permesso di Statistiche) */}
                  <Route path="analytics" element={
                    <PermissionProtectedRoute permissionKey="view_stats">
                      <AnalyticsPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* System Health - view_system_health */}
                  <Route path="system-health" element={
                    <PermissionProtectedRoute permissionKey="view_system_health">
                      <SystemHealthPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Audit Log - view_audit_logs */}
                  <Route path="audit-logs" element={
                    <PermissionProtectedRoute permissionKey="view_audit_logs">
                      <AuditLogPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Data Integrity - view_data_integrity */}
                  <Route path="data-integrity" element={
                    <PermissionProtectedRoute permissionKey="view_data_integrity">
                      <DataIntegrityPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* WhatsApp - view_whatsapp */}
                  <Route path="whatsapp" element={
                    <PermissionProtectedRoute permissionKey="view_whatsapp">
                      <WhatsAppPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Provvigioni - view_commissions */}
                  <Route path="commissions" element={
                    <PermissionProtectedRoute permissionKey="view_commissions">
                      <CommissionsPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Profilo - sempre accessibile */}
                  <Route path="profile" element={<ProfilePage />} />
                  
                  {/* Extra progetti - manage_projects */}
                  <Route path="projects/:projectId/extras/new" element={
                    <PermissionProtectedRoute permissionKey="manage_projects">
                      <ExtraDetailPage />
                    </PermissionProtectedRoute>
                  } />
                  <Route path="projects/:projectId/extras/:id" element={
                    <PermissionProtectedRoute permissionKey="manage_projects">
                      <ExtraDetailPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Pagamenti - view_payments */}
                  <Route path="payments" element={
                    <PermissionProtectedRoute permissionKey="view_payments">
                      <PaymentsDashboardPage />
                    </PermissionProtectedRoute>
                  } />
                  
                  {/* Arredi - manage_furniture */}
                  <Route path="furniture" element={
                    <PermissionProtectedRoute permissionKey="manage_furniture">
                      <FurnitureDashboardPage />
                    </PermissionProtectedRoute>
                  } />
                </Route>

                {/* Catch all - redirect to dashboard */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </ErrorBoundary>
              <Toaster position="top-right" richColors />
            </div>
          </AppInitializer>
        </NavigationGuardProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
